import { Button, Icon, type Option, SingleSelect } from "@appretio/ui-kit";
import * as styles from "./AlgorithmicForecastDialog.css";
import { findOption } from "./algorithmicForecastOptions";
import type { RangeWeekOptions } from "./gridWeekOptions";
import type { GenerateInputErrors } from "./validateGenerateInputs";

/** Props for the load dialog's collapsed Advanced-options block, one object per control group. */
export interface AdvancedOptionsSectionProps {
  /** The disclosure's expanded state and its toggle. */
  expanded: { value: boolean; onChange: (next: boolean) => void };
  /** Training range and forecast horizon. */
  weeks: {
    /** Selectable weeks per picker, narrowed so no pick breaks the range. */
    options: RangeWeekOptions;
    /** Training-range start week. */
    trainFrom: Option | undefined;
    /** Notified with the newly selected training-range start. */
    onTrainFromChange: (next: Option | undefined) => void;
    /** Training-range end week. */
    trainTo: Option | undefined;
    /** Notified with the newly selected training-range end. */
    onTrainToChange: (next: Option | undefined) => void;
    /** Forecast-horizon end week. */
    forecastTo: Option | undefined;
    /** Notified with the newly selected horizon end. */
    onForecastToChange: (next: Option | undefined) => void;
  };
  /** Field errors from the last submit attempt. */
  errors: Pick<
    GenerateInputErrors,
    "trainWeekFrom" | "trainWeekTo" | "forecastWeekTo"
  >;
  /** Disables every field while a generate is in flight. */
  isPending: boolean;
}

const FIELDS_ID = "algorithmic-forecast-advanced-options";

/**
 * The dialog-local Advanced-options disclosure: the ground truth (always
 * actuals, shown not chosen), training range and forecast horizon. A tertiary `Button` + chevron `Icon` + conditional
 * render, per the SPEC — the ui-kit has no collapsible primitive, and one
 * consumer doesn't earn one.
 */
export function AdvancedOptionsSection({
  expanded,
  weeks,
  errors,
  isPending
}: AdvancedOptionsSectionProps) {
  return (
    <div className={styles.advancedOptions}>
      <Button
        type="button"
        variant="tertiary"
        size="sm"
        aria-expanded={expanded.value}
        aria-controls={FIELDS_ID}
        onClick={() => expanded.onChange(!expanded.value)}
      >
        <Icon name={expanded.value ? "chevron-up" : "chevron-down"} />
        Advanced options
      </Button>
      {expanded.value && (
        <div
          id={FIELDS_ID}
          className={styles.advancedOptionsFields}
          data-testid="advanced-options-fields"
        >
          <div className={styles.groundTruth}>Ground truth: Actuals</div>

          <div className={styles.weekRangeRow}>
            <div className={styles.weekRangeItem}>
              <SingleSelect
                label="Training range from"
                placeholder="Training range from"
                options={weeks.options.trainFrom}
                selectedOption={weeks.trainFrom}
                onChange={(value) =>
                  weeks.onTrainFromChange(
                    findOption(weeks.options.trainFrom, value)
                  )
                }
                error={errors.trainWeekFrom}
                clearable={false}
                disabled={isPending}
                modalPopover
                size="md"
              />
            </div>
            <div className={styles.weekRangeItem}>
              <SingleSelect
                label="Training range to"
                placeholder="Training range to"
                options={weeks.options.trainTo}
                selectedOption={weeks.trainTo}
                onChange={(value) =>
                  weeks.onTrainToChange(
                    findOption(weeks.options.trainTo, value)
                  )
                }
                error={errors.trainWeekTo}
                clearable={false}
                disabled={isPending}
                modalPopover
                size="md"
              />
            </div>
          </div>

          <SingleSelect
            label="Forecast horizon to"
            placeholder="Forecast horizon to"
            options={weeks.options.forecastTo}
            error={errors.forecastWeekTo}
            selectedOption={weeks.forecastTo}
            onChange={(value) =>
              weeks.onForecastToChange(
                findOption(weeks.options.forecastTo, value)
              )
            }
            clearable={false}
            disabled={isPending}
            modalPopover
            size="md"
          />
        </div>
      )}
    </div>
  );
}
